import { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Col, Row } from 'react-bootstrap'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { selectDocument, selectDocumentIsNotRelatedToClient, selectDocumentLoading } from '../../../store/processDocument/document'
import {
  selectDocumentBatchContact,
  selectDocumentBatchContactIsLoading,
  selectDocumentBatchHasMultipleDocuments,
  selectDocumentBatchLoading,
} from '../../../store/processDocument/documentBatch'
import { selectIsHistoryMode } from '../../../store/meetingSummary/contactUpdate'
import { useAppSelector } from '../../../store/hooks'

import { ExternalUpdateHistoryView } from '../../UpdateExternalContact/ExternalUpdateHistoryView'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'

import { DocumentSummary } from '../DocumentSummary/DocumentSummary'
import { DocumentSummaryContacts } from '../DocumentSummary/DocumentSummaryContacts'
import { DocumentBatchChat } from '../DocumentBatchChat/DocumentBatchChat'
import { DocumentSidebar } from '../DocumentSidebar/DocumentSidebar'

import { Spinner } from '../../../components/Spinner/Spinner'
import { DocumentWorkflowResults } from '../../../components/CustomWorkflows/entityResults/DocumentWorkflowResults'

import { DocumentBatchNavigation } from './DocumentBatchNavigation'
import { DocumentHeader } from './DocumentHeader'
import { DocumentDetails } from './DocumentDetails'

import './Document.scss'

interface DocumentProps {
  documentId: string
}

export const Document: React.FC<DocumentProps> = ({ documentId }) => {
  const document = useAppSelector(selectDocument)
  const contact = useAppSelector(selectDocumentBatchContact)
  const isHistoryMode = useAppSelector(selectIsHistoryMode)
  const documentIsNotRelatedToClient = useAppSelector(selectDocumentIsNotRelatedToClient)
  const hasMultipleDocuments = useAppSelector(selectDocumentBatchHasMultipleDocuments)

  const documentLoading = useAppSelector(selectDocumentLoading)
  const documentBatchLoading = useAppSelector(selectDocumentBatchLoading)
  const contactLoading = useAppSelector(selectDocumentBatchContactIsLoading)

  const { portalRef } = useContext(HeaderContext)

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  if (!document && !documentLoading) {
    return null
  }

  if (documentBatchLoading || contactLoading) {
    return <DocumentLoader />
  }

  if (!contact) {
    return null
  }

  return (
    <>
      {headerContainer ? createPortal(<DocumentHeader />, headerContainer) : null}
      {isHistoryMode ? (
        <ExternalUpdateHistoryView />
      ) : (
        <Row>
          <Col className='d-flex flex-column zocks-gap-3'>
            <div className='d-flex flex-column gap-3'>
              {hasMultipleDocuments ? <DocumentBatchNavigation documentId={documentId} /> : null}
              <DocumentDetails contact={contact} />
            </div>
            <Row>
              <Col xs={7} className='d-flex flex-column gap-3'>
                <DocumentSummary />
              </Col>
              <Col xs={5} className='d-flex flex-column gap-4'>
                {documentIsNotRelatedToClient ? <DocumentIsNotRelatedToClientBadge contactName={getUserDisplayName(contact)} /> : null}
                <DocumentSummaryContacts contact={contact} />
                <DocumentBatchChat contact={contact} />
                <DocumentWorkflowResults key={documentId} documentId={documentId} />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <DocumentSidebar />
    </>
  )
}

const DocumentLoader = () => {
  return (
    <div className='document-loader d-flex flex-column justify-content-center align-items-center flex-shrink-0 align-self-stretch p-4 zocks-gap-1.5 zocks-border-0.75'>
      <Spinner />
      <span className='paragraph-small medium zocks-text-gray-900'>Loading document…</span>
    </div>
  )
}

interface DocumentIsNotRelatedToClientBadgeProps {
  contactName: string
}

const DocumentIsNotRelatedToClientBadge: React.FC<DocumentIsNotRelatedToClientBadgeProps> = ({ contactName }) => {
  return (
    <div className='d-flex align-items-center gap-3 p-3 zocks-bg-color-warning rounded-3'>
      <i className='bi bi-exclamation-triangle fs-5' />
      <span className='paragraph-medium'>This document may not be related to {contactName}. Review the summary and insights before syncing.</span>
    </div>
  )
}
